import { useCallback, useMemo, useState } from 'react';
const ALL_STEPS = [
    'intro',
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'secureLogin',
    'integrations',
    'qboXeroIntegration',
    'waiting',
    'closing'
];
export const deriveVobMode = (param)=>{
    let { sp_onboarding_phase_1, isClient, hasCustomerWorkspace, hasCompletedVendorWorkspace, hasIdentity } = param;
    if (!sp_onboarding_phase_1) return 'default';
    if (!hasIdentity) return 'clientToVendor';
    if (hasCompletedVendorWorkspace) return 'additionalAccount';
    if (isClient || hasCustomerWorkspace) return 'clientToVendor';
    return 'default';
};
const DEFAULT_STEPS = [
    'intro',
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'integrations',
    'qboXeroIntegration',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'waiting',
    'closing'
];
const ADDITIONAL_ACCOUNT_STEPS = DEFAULT_STEPS.filter((step)=>step !== 'intro' && step !== 'intent' && step !== 'waiting');
const CLIENT_TO_VENDOR_STEPS = (()=>{
    const brandIdx = DEFAULT_STEPS.indexOf('brand');
    return [
        ...DEFAULT_STEPS.slice(0, brandIdx + 1),
        'secureLogin',
        ...DEFAULT_STEPS.slice(brandIdx + 1)
    ];
})();
const STEPS_BY_MODE = {
    default: DEFAULT_STEPS,
    additionalAccount: ADDITIONAL_ACCOUNT_STEPS,
    clientToVendor: CLIENT_TO_VENDOR_STEPS
};
const DATA_COLLECTION_STEPS = new Set([
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'secureLogin',
    'integrations',
    'qboXeroIntegration'
]);
const SKIPPABLE_STEPS = new Set([
    'intent',
    'integrations',
    'inviteTeam',
    'businessWebsite',
    'brand'
]);
export const pickInitialStepFromBusinessData = (param)=>{
    let { hasBusiness, hasPostalAddress, hasDescription, hasIdentity, mode } = param;
    if (!hasBusiness) return null;
    if (!hasPostalAddress) return 'businessDetailsA';
    if (!hasDescription) return 'businessDetailsB';
    if (!hasIdentity && STEPS_BY_MODE[mode].includes('secureLogin')) return 'secureLogin';
    return null;
};
const PROGRESS_STORAGE_KEY = 'anchor.vendorOnboarding.progress';
const LOGO_STORAGE_KEY = 'anchor.vendorOnboarding.logo';
const INTRO_PHASE_STORAGE_KEY = 'anchor.vendorOnboarding.introPhase';
const tryRead = (key)=>{
    try {
        return sessionStorage.getItem(key);
    } catch (unused) {
        return null;
    }
};
const tryWrite = (key, value)=>{
    try {
        sessionStorage.setItem(key, value);
    } catch (unused) {
    /* */ }
};
const tryRemove = (key)=>{
    try {
        sessionStorage.removeItem(key);
    } catch (unused) {
    /* */ }
};
export const readPersistedProgress = (currentUserId)=>{
    const value = tryRead(PROGRESS_STORAGE_KEY);
    if (value == null) return null;
    try {
        const parsed = JSON.parse(value);
        if (currentUserId != null && parsed.userId != null && parsed.userId !== currentUserId) {
            tryRemove(PROGRESS_STORAGE_KEY);
            tryRemove(LOGO_STORAGE_KEY);
            tryRemove(INTRO_PHASE_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch (unused) {
        return null;
    }
};
export const persistProgress = (progress)=>{
    tryWrite(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
};
export const readPersistedLogo = (currentUserId)=>{
    const value = tryRead(LOGO_STORAGE_KEY);
    if (value == null) return null;
    if (!value.startsWith('{')) return value;
    try {
        const envelope = JSON.parse(value);
        if (currentUserId != null && envelope.userId !== currentUserId) {
            tryRemove(LOGO_STORAGE_KEY);
            return null;
        }
        return envelope.logo;
    } catch (unused) {
        return null;
    }
};
export const persistLogo = (logo, userId)=>{
    if (logo == null) {
        tryRemove(LOGO_STORAGE_KEY);
        return;
    }
    if (userId == null) {
        tryWrite(LOGO_STORAGE_KEY, logo);
        return;
    }
    const envelope = {
        userId,
        logo
    };
    tryWrite(LOGO_STORAGE_KEY, JSON.stringify(envelope));
};
export const readPersistedCreditsAwarded = ()=>{
    var _ref;
    var _readPersistedProgress;
    return (_ref = (_readPersistedProgress = readPersistedProgress()) === null || _readPersistedProgress === void 0 ? void 0 : _readPersistedProgress.creditsAwarded) !== null && _ref !== void 0 ? _ref : false;
};
export const readPersistedIntroIsOnCreditsPhase = ()=>tryRead(INTRO_PHASE_STORAGE_KEY) === 'credits';
export const persistIntroIsOnCreditsPhase = (isOnCreditsPhase)=>{
    if (isOnCreditsPhase) {
        tryWrite(INTRO_PHASE_STORAGE_KEY, 'credits');
    } else {
        tryRemove(INTRO_PHASE_STORAGE_KEY);
    }
};
export const readPersistedCurrentStep = ()=>{
    var _readPersistedProgress;
    const step = (_readPersistedProgress = readPersistedProgress()) === null || _readPersistedProgress === void 0 ? void 0 : _readPersistedProgress.currentStep;
    if (step == null) return null;
    return ALL_STEPS.includes(step) ? step : null;
};
export const shouldResumeVendorOnboarding = (param)=>{
    let { sp_onboarding_phase_1, persistedStep } = param;
    if (!sp_onboarding_phase_1) return false;
    return persistedStep != null;
};
export const clearPersistedOnboardingProgress = ()=>{
    tryRemove(PROGRESS_STORAGE_KEY);
    tryRemove(LOGO_STORAGE_KEY);
    tryRemove(INTRO_PHASE_STORAGE_KEY);
};
export const useOnboardingFlow = (options)=>{
    var _ref;
    const mode = (_ref = options === null || options === void 0 ? void 0 : options.mode) !== null && _ref !== void 0 ? _ref : 'default';
    const baseSteps = STEPS_BY_MODE[mode];
    const activeSteps = useMemo(()=>{
        return baseSteps.filter((step)=>{
            if ((options === null || options === void 0 ? void 0 : options.skipInviteTeam) && step === 'inviteTeam') {
                return false;
            }
            if (((options === null || options === void 0 ? void 0 : options.skipQboXero) || (options === null || options === void 0 ? void 0 : options.isMobile)) && step === 'qboXeroIntegration') {
                return false;
            }
            if ((options === null || options === void 0 ? void 0 : options.skipSecureLogin) && step === 'secureLogin') {
                return false;
            }
            return true;
        });
    }, [
        baseSteps,
        options === null || options === void 0 ? void 0 : options.skipInviteTeam,
        options === null || options === void 0 ? void 0 : options.skipQboXero,
        options === null || options === void 0 ? void 0 : options.skipSecureLogin,
        options === null || options === void 0 ? void 0 : options.isMobile
    ]);
    const [stepIndex, setStepIndex] = useState(()=>{
        const persistedStep = readPersistedCurrentStep();
        if (persistedStep != null) {
            const persistedIdx = activeSteps.indexOf(persistedStep);
            if (persistedIdx >= 0) return persistedIdx;
            const persistedBaseIdx = baseSteps.indexOf(persistedStep);
            if (persistedBaseIdx >= 0) {
                for(let i = persistedBaseIdx + 1; i < baseSteps.length; i++){
                    const nextActiveIdx = activeSteps.indexOf(baseSteps[i]);
                    if (nextActiveIdx >= 0) return nextActiveIdx;
                }
            }
        }
        if ((options === null || options === void 0 ? void 0 : options.initialStepOverride) != null) {
            const overrideIdx = activeSteps.indexOf(options.initialStepOverride);
            if (overrideIdx >= 0) return overrideIdx;
        }
        if (!readPersistedCreditsAwarded()) return 0;
        const idx = activeSteps.indexOf('businessDetailsA');
        return idx >= 0 ? idx : 0;
    });
    const clampedIndex = Math.min(stepIndex, activeSteps.length - 1);
    const currentStep = activeSteps[clampedIndex];
    const progressSteps = useMemo(()=>baseSteps.filter((step)=>DATA_COLLECTION_STEPS.has(step)), [
        baseSteps
    ]);
    const totalDataCollectionSteps = progressSteps.length;
    const dataCollectionIndex = progressSteps.indexOf(currentStep);
    const isDataCollectionStep = dataCollectionIndex >= 0;
    const isSkippableStep = SKIPPABLE_STEPS.has(currentStep);
    const prevStep = clampedIndex > 0 ? activeSteps[clampedIndex - 1] : null;
    const isBackDisabled = clampedIndex === 0 || prevStep === 'businessDetailsA' || prevStep === 'businessDetailsB';
    const progress = isDataCollectionStep ? (dataCollectionIndex + 1) / (totalDataCollectionSteps + 1) * 100 : 0;
    const goNext = useCallback(()=>{
        setStepIndex((prev)=>Math.min(prev + 1, activeSteps.length - 1));
    }, [
        activeSteps.length
    ]);
    const goBack = useCallback(()=>{
        setStepIndex((prev)=>Math.max(prev - 1, 0));
    }, []);
    return {
        currentStep,
        progress,
        isDataCollectionStep,
        isSkippableStep,
        isBackDisabled,
        goNext,
        goBack
    };
};
